import { useState, useCallback } from 'react';
import {
  Stack, Text, TextInput, Alert, Button, Group, Card, Title, Divider, Badge, HtmlEditor,
} from '@/components/ui';
import { JsonInput } from '@mantine/core';
import { IconAlertCircle, IconCheck, IconEdit, IconSparkles } from '@tabler/icons-react';
import { useAiWorkflowService } from '@actschurch/ccms-sdk/react';
import { useAiWorkflowStore } from '../../../../stores/aiWorkflowStore';

export default function ReviewExtractedData() {
  const workflowService = useAiWorkflowService();
  const {
    activeSessionId,
    selectedPresetName,
    targetRecordType,
    extractedData,
    validationErrors,
    setExtractedData,
    setCurrentStep,
    setError,
    error,
    reset,
  } = useAiWorkflowStore();

  const [editedFields, setEditedFields] = useState<Record<string, unknown>>(
    extractedData ?? {}
  );
  const [rawJsonMode, setRawJsonMode] = useState(false);
  const [rawJson, setRawJson] = useState(
    extractedData ? JSON.stringify(extractedData, null, 2) : '{}'
  );
  const [submitting, setSubmitting] = useState(false);

  const handleFieldChange = useCallback((key: string, value: string) => {
    setEditedFields((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!activeSessionId) return;
    setSubmitting(true);
    setError(null);

    try {
      const jsonToSend = rawJsonMode
        ? rawJson
        : JSON.stringify(editedFields);

      JSON.parse(jsonToSend);

      await workflowService.confirm(activeSessionId, jsonToSend);
      setCurrentStep('confirm');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to confirm data';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }, [activeSessionId, rawJsonMode, rawJson, editedFields, workflowService, setCurrentStep, setError]);

  const handleReExtract = useCallback(() => {
    setExtractedData(null);
    setCurrentStep('upload');
  }, [setExtractedData, setCurrentStep]);

  const renderFields = () => {
    if (!editedFields || Object.keys(editedFields).length === 0) {
      return (
        <Alert color="yellow">
          No structured data was extracted. You can switch to JSON mode to enter data manually.
        </Alert>
      );
    }

    return Object.entries(editedFields).map(([key, value]) => {
      const strValue = typeof value === 'object' ? JSON.stringify(value) : String(value ?? '');
      const isLongValue = strValue.length > 100;

      return isLongValue ? (
        <HtmlEditor
          key={key}
          label={formatLabel(key)}
          value={strValue}
          onChange={(_html, text) => handleFieldChange(key, text)}
          minHeight={100}
        />
      ) : (
        <TextInput
          key={key}
          label={formatLabel(key)}
          value={strValue}
          onChange={(e) => handleFieldChange(key, e.currentTarget.value)}
        />
      );
    });
  };

  return (
    <Stack gap="lg">
      {/* Summary card at top */}
      <Card shadow="sm" padding="lg" radius="md" withBorder bg="blue.0">
        <Group justify="space-between" mb="sm">
          <Group gap="xs">
            <IconSparkles size={20} />
            <Title order={4}>Extraction Summary</Title>
          </Group>
          <Button
            variant="subtle"
            size="xs"
            onClick={() => setRawJsonMode(!rawJsonMode)}
          >
            {rawJsonMode ? 'Field View' : 'JSON View'}
          </Button>
        </Group>
        <Divider mb="sm" />
        <Group gap="lg">
          <Group gap="xs">
            <Text size="sm" fw={500}>Preset:</Text>
            <Text size="sm">{selectedPresetName}</Text>
          </Group>
          <Group gap="xs">
            <Text size="sm" fw={500}>Target:</Text>
            <Badge variant="light" color="blue">{targetRecordType}</Badge>
          </Group>
        </Group>
      </Card>

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <Alert icon={<IconAlertCircle size={16} />} title="Validation Issues" color="orange">
          <Stack gap={4}>
            {validationErrors.map((err, i) => (
              <Text key={i} size="sm">{err}</Text>
            ))}
          </Stack>
        </Alert>
      )}

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Form fields card */}
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="sm">
          <Group gap="xs">
            <IconEdit size={20} />
            <Title order={4}>Extracted Data</Title>
          </Group>
          <Badge variant="light" color="gray">
            {Object.keys(editedFields).length} fields
          </Badge>
        </Group>
        <Divider mb="sm" />
        <Stack gap="sm">
          {rawJsonMode ? (
            <JsonInput
              label="Extracted JSON"
              description="Edit the raw JSON data directly."
              value={rawJson}
              onChange={setRawJson}
              minRows={10}
              autosize
              formatOnBlur
              validationError="Invalid JSON"
            />
          ) : (
            renderFields()
          )}
        </Stack>
      </Card>

      {/* Action buttons outside cards */}
      <Group justify="space-between">
        <Group gap="xs">
          <Button variant="default" onClick={reset}>
            Cancel
          </Button>
          <Button variant="light" onClick={handleReExtract}>
            Re-extract
          </Button>
        </Group>
        <Button
          onClick={handleConfirm}
          loading={submitting}
          leftSection={<IconCheck size={16} />}
        >
          Looks Good
        </Button>
      </Group>
    </Stack>
  );
}

/** Convert camelCase/snake_case keys to human-readable labels */
function formatLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}
